import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { formatCurrency } from '../../utils/formatters';
import {
  generateAmortizationSchedule,
  calculateTotalInterest,
  calculateOverpaymentImpact,
  getMortgagePaymentInfo,
  calculateRemainingTerm,
  generatePropertyMortgageProjection,
} from '../../utils/mortgageCalculations';
import {
  ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis,
  Tooltip, Legend, CartesianGrid, BarChart, Bar, ReferenceLine,
} from 'recharts';
import dayjs from 'dayjs';

const MortgageDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { mortgages, properties } = useData();
  const [overpaymentAmount, setOverpaymentAmount] = useState(0);
  const [showFullSchedule, setShowFullSchedule] = useState(false);

  const mortgage = mortgages.find(m => m.id === id);
  if (!mortgage) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <p className="text-gray-600">Mortgage not found.</p>
        <button onClick={() => navigate('/mortgages')} className="text-blue-600 hover:underline mt-2">Back to Mortgages</button>
      </div>
    );
  }

  const linkedProperty = properties.find(p => p.id === mortgage.linkedPropertyId);
  const paymentInfo = getMortgagePaymentInfo(mortgage);
  const totalInterestInfo = calculateTotalInterest(mortgage);
  const schedule = generateAmortizationSchedule(mortgage);
  const remainingMonths = calculateRemainingTerm(mortgage);

  // Overpayment impact
  const overpaymentImpact = overpaymentAmount > 0
    ? calculateOverpaymentImpact(mortgage, overpaymentAmount)
    : null;

  // Chart data - thin out for large schedules
  const step = schedule.length > 120 ? 6 : schedule.length > 60 ? 3 : 1;
  const balanceChartData = schedule.filter((_, i) => i % step === 0 || i === schedule.length - 1);

  // Payment breakdown chart data (yearly aggregates)
  const yearlyBreakdown = [];
  const startYear = dayjs().year();
  for (let i = 0; i < schedule.length; i += 12) {
    const yearSlice = schedule.slice(i, Math.min(i + 12, schedule.length));
    const yearInterest = yearSlice.reduce((s, m) => s + m.interest, 0);
    const yearPrincipal = yearSlice.reduce((s, m) => s + m.principal, 0);
    yearlyBreakdown.push({
      year: startYear + Math.floor(i / 12),
      interest: yearInterest,
      principal: yearPrincipal,
      total: yearInterest + yearPrincipal,
    });
  }

  // Combined property vs mortgage projection
  const projectionData = linkedProperty
    ? generatePropertyMortgageProjection(linkedProperty, mortgage)
    : null;
  const projectionChartData = projectionData
    ? projectionData.filter((_, i) => i % step === 0 || i === projectionData.length - 1)
    : null;

  const fixedEnd = dayjs(mortgage.fixedTermEndDate);
  const fixedEndMonth = schedule.findIndex(s => dayjs(s.date).isAfter(fixedEnd));

  const formatYAxis = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
    return value;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6">
        <button onClick={() => navigate('/mortgages')} className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1">
          ← Back to Mortgages
        </button>
        <h1 className="text-2xl font-bold text-gray-900 mt-2">{mortgage.name}</h1>
        {mortgage.lender && <p className="text-sm text-gray-500 mt-1">{mortgage.lender}</p>}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Balance</p>
          <p className="text-xl font-bold text-red-600 mt-1">{formatCurrency(mortgage.currentBalance, mortgage.currency)}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Monthly Payment</p>
          <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(paymentInfo.monthlyPayment, mortgage.currency)}</p>
          {mortgage.overpaymentMonthly > 0 && (
            <p className="text-xs text-gray-500">+ {formatCurrency(mortgage.overpaymentMonthly, mortgage.currency)} overpayment</p>
          )}
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Current Rate</p>
          <p className="text-xl font-bold text-gray-900 mt-1">{paymentInfo.currentRate}%</p>
          <p className={`text-xs ${paymentInfo.isInFixedPeriod ? 'text-green-600' : 'text-amber-600'}`}>
            {paymentInfo.isInFixedPeriod ? `Fixed until ${fixedEnd.format('MMM YYYY')}` : 'SVR'}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Remaining Term</p>
          <p className="text-xl font-bold text-gray-900 mt-1">{Math.floor(remainingMonths / 12)}y {remainingMonths % 12}m</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Total Interest</p>
          <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(totalInterestInfo.totalInterest, mortgage.currency)}</p>
        </div>
      </div>

      {/* Rate Timeline */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Rate Timeline</h3>
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <div className="flex h-8 rounded-lg overflow-hidden">
              {paymentInfo.isInFixedPeriod ? (
                <>
                  <div
                    className="flex items-center justify-center text-xs font-medium text-white"
                    style={{
                      backgroundColor: '#059669',
                      width: `${Math.max(10, (paymentInfo.fixedMonthsRemaining / remainingMonths) * 100)}%`,
                    }}
                  >
                    {mortgage.fixedRate}% Fixed
                  </div>
                  <div
                    className="flex items-center justify-center text-xs font-medium text-white"
                    style={{
                      backgroundColor: '#d97706',
                      flex: 1,
                    }}
                  >
                    {mortgage.svrRate}% SVR
                  </div>
                </>
              ) : (
                <div
                  className="flex items-center justify-center text-xs font-medium text-white w-full"
                  style={{ backgroundColor: '#d97706' }}
                >
                  {mortgage.svrRate}% SVR (Fixed period ended)
                </div>
              )}
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-xs text-gray-400">Now</span>
              {paymentInfo.isInFixedPeriod && (
                <span className="text-xs text-gray-400">{fixedEnd.format('MMM YYYY')}</span>
              )}
              <span className="text-xs text-gray-400">
                {dayjs(mortgage.startDate).add(mortgage.termYears, 'year').format('MMM YYYY')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Balance Over Time Chart */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Mortgage Balance Over Time</h3>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={balanceChartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="displayDate" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
            <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(value, name) => [formatCurrency(value, mortgage.currency), name]} />
            <Legend />
            <Area type="monotone" dataKey="balance" fill="#fee2e2" stroke="#dc2626" strokeWidth={2} name="Outstanding Balance" />
            {fixedEndMonth > 0 && (
              <ReferenceLine
                x={balanceChartData.find((_, i) => i >= fixedEndMonth / step)?.displayDate}
                stroke="#d97706"
                strokeDasharray="5 5"
                label={{ value: 'Fixed → SVR', position: 'top', fontSize: 10, fill: '#d97706' }}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Payment Breakdown Chart (Yearly) */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Annual Payment Breakdown</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={yearlyBreakdown}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="year" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(value, name) => [formatCurrency(value, mortgage.currency), name]} />
            <Legend />
            <Bar dataKey="principal" stackId="a" fill="#059669" name="Principal" />
            <Bar dataKey="interest" stackId="a" fill="#dc2626" name="Interest" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Property vs Mortgage Combined Chart */}
      {linkedProperty && projectionChartData && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900">Property Value vs Mortgage Balance</h3>
            <button
              onClick={() => navigate(`/properties/${linkedProperty.id}`)}
              className="text-xs text-blue-600 hover:underline"
            >
              View Property →
            </button>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={projectionChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="displayDate" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(value, name) => [formatCurrency(value, mortgage.currency), name]} />
              <Legend />
              <Area type="monotone" dataKey="equity" fill="#bfdbfe" stroke="none" name="Equity" />
              <Line type="monotone" dataKey="propertyValue" stroke="#059669" strokeWidth={2} dot={false} name="Property Value" />
              <Line type="monotone" dataKey="mortgageBalance" stroke="#dc2626" strokeWidth={2} dot={false} name="Mortgage Balance" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Overpayment Calculator */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Overpayment Calculator</h3>
        <div className="flex items-end gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Monthly Overpayment</label>
            <input
              type="number"
              step="50"
              min="0"
              value={overpaymentAmount}
              onChange={(e) => setOverpaymentAmount(parseFloat(e.target.value) || 0)}
              className="block w-40 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="0"
            />
          </div>
          <div className="flex gap-2">
            {[100, 200, 500].map(amt => (
              <button
                key={amt}
                onClick={() => setOverpaymentAmount(amt)}
                className={`px-3 py-1.5 text-xs rounded-lg ${overpaymentAmount === amt ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                style={overpaymentAmount === amt ? { backgroundColor: '#012E58' } : {}}
              >
                {formatCurrency(amt, mortgage.currency)}
              </button>
            ))}
          </div>
        </div>

        {overpaymentImpact && overpaymentAmount > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-xs text-green-700">Months Saved</p>
              <p className="text-lg font-bold text-green-800">
                {overpaymentImpact.monthsSaved}
                <span className="text-xs font-normal ml-1">
                  ({Math.floor(overpaymentImpact.monthsSaved / 12)}y {overpaymentImpact.monthsSaved % 12}m)
                </span>
              </p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-xs text-green-700">Interest Saved</p>
              <p className="text-lg font-bold text-green-800">{formatCurrency(overpaymentImpact.interestSaved, mortgage.currency)}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-xs text-gray-500">New Term</p>
              <p className="text-lg font-bold text-gray-900">
                {Math.floor(overpaymentImpact.newTermMonths / 12)}y {overpaymentImpact.newTermMonths % 12}m
              </p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-xs text-gray-500">Total Monthly Cost</p>
              <p className="text-lg font-bold text-gray-900">
                {formatCurrency(paymentInfo.monthlyPayment + overpaymentAmount, mortgage.currency)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Amortization Schedule Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900">Amortization Schedule</h3>
          <button
            onClick={() => setShowFullSchedule(!showFullSchedule)}
            className="text-xs text-blue-600 hover:underline"
          >
            {showFullSchedule ? 'Show Summary' : `Show All ${schedule.length} Months`}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 pr-3 text-gray-500 font-medium">Date</th>
                <th className="text-right py-2 px-3 text-gray-500 font-medium">Payment</th>
                <th className="text-right py-2 px-3 text-gray-500 font-medium">Principal</th>
                <th className="text-right py-2 px-3 text-gray-500 font-medium">Interest</th>
                <th className="text-right py-2 px-3 text-gray-500 font-medium">Balance</th>
                <th className="text-right py-2 pl-3 text-gray-500 font-medium">Rate</th>
              </tr>
            </thead>
            <tbody>
              {(showFullSchedule ? schedule : schedule.filter((_, i) => i % 12 === 0 || i === schedule.length - 1)).map((entry, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-1.5 pr-3 text-gray-700">{entry.displayDate}</td>
                  <td className="py-1.5 px-3 text-right text-gray-900">{formatCurrency(entry.payment, mortgage.currency)}</td>
                  <td className="py-1.5 px-3 text-right text-green-700">{formatCurrency(entry.principal, mortgage.currency)}</td>
                  <td className="py-1.5 px-3 text-right text-red-600">{formatCurrency(entry.interest, mortgage.currency)}</td>
                  <td className="py-1.5 px-3 text-right font-medium text-gray-900">{formatCurrency(entry.balance, mortgage.currency)}</td>
                  <td className="py-1.5 pl-3 text-right text-gray-500">{entry.rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MortgageDetail;
